import { useMemo, useState } from 'react';

import { createMandatoryContext } from '@change-corgi/core-react/context';

import { useUploadStepContext } from '#src/app/pages/actions/shared/hooks';

import type { PetitionVideo, UploadStep } from './types';

/** @knip-ignore */
export type VideoContextData = {
	stream: MediaStream | null;
	videoElement: HTMLVideoElement | null;
	blob: Blob | null;
	/**
	 * Wall-clock length of the last recording, in seconds. Used as a fallback when the recorded blob's own
	 * duration cannot be read before upload. Undefined for videos chosen from the device.
	 */
	recordedDurationSec: number | undefined;
	isRecording: boolean;
};

export type VideoContextValueType = {
	data: VideoContextData;
	actions: {
		setStream: React.Dispatch<React.SetStateAction<MediaStream | null>>;
		setVideoElement: React.Dispatch<React.SetStateAction<HTMLVideoElement | null>>;
		setBlob: React.Dispatch<React.SetStateAction<Blob | null>>;
		setRecordedDurationSec: React.Dispatch<React.SetStateAction<number | undefined>>;
		setIsRecording: React.Dispatch<React.SetStateAction<boolean>>;
	};
};

/** @knip-ignore */
export const {
	Context: VideoContext,
	Provider: VideoContextProviderInternal,
	useContext: useVideoContext,
} = createMandatoryContext<VideoContextValueType>(undefined, {
	name: 'VideoContext',
});

/**
 * Builds the `VideoContext` value: state for the video recorder (and recording playback) —
 * stream, video element, recorded blob, and recorded duration — plus the setters for each.
 * This is kept separate from `VideosComponentContext` (which holds submission/upload-step data)
 * for legacy reasons, though the two could have been consolidated.
 */
export const useVideoContextValue = () => {
	const [stream, setStream] = useState<MediaStream | null>(null);
	const [videoElement, setVideoElement] = useState<HTMLVideoElement | null>(null);
	const [blob, setBlob] = useState<Blob | null>(null);
	const [recordedDurationSec, setRecordedDurationSec] = useState<number | undefined>(undefined);
	const [isRecording, setIsRecording] = useState<boolean>(false);

	const value: VideoContextValueType = useMemo(
		() => ({
			data: {
				stream,
				videoElement,
				blob,
				recordedDurationSec,
				isRecording,
			},
			actions: {
				setStream,
				setVideoElement,
				setBlob,
				setRecordedDurationSec,
				setIsRecording,
			},
		}),
		[stream, videoElement, blob, recordedDurationSec, isRecording],
	);
	return { value };
};

/** @knip-ignore */
export type VideosComponentContextData = {
	petitionVideoList: PetitionVideo[];
	initialPetitionVideoListFetchNeeded: boolean;
	isUploading: boolean;
	uploadStep: UploadStep;
	errorMessage: string | null;
	isSuccessMessageDisplayed: boolean;
	uploadedVideoId: string;
	videoSource: string;
	/**
	 * Whether the video-first ask screen has already played its entrance animation
	 * (`supporter_voices_video_first`, arms v1_emotional/v3_impact). The ask screen unmounts when the
	 * flow advances to the recorder, so a component-local ref would replay the cascade every time the
	 * supporter came back. This context outlives the individual steps, which makes it the right scope
	 * for "this has been seen once".
	 */
	hasPlayedAskIntro: boolean;
};

export type VideosComponentContextValueType = {
	data: VideosComponentContextData;
	actions: {
		setPetitionVideoList: React.Dispatch<React.SetStateAction<PetitionVideo[]>>;
		setInitialPetitionVideoListFetchNeeded: React.Dispatch<React.SetStateAction<boolean>>;
		setUploadStep: React.Dispatch<React.SetStateAction<UploadStep>>;
		setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
		setIsSuccessMessageDisplayed: React.Dispatch<React.SetStateAction<boolean>>;
		setUploadedVideoId: React.Dispatch<React.SetStateAction<string>>;
		setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
		setVideoSource: React.Dispatch<React.SetStateAction<string>>;
		setHasPlayedAskIntro: React.Dispatch<React.SetStateAction<boolean>>;
	};
};

/** @knip-ignore */
export const {
	Context: VideosComponentContext,
	Provider: VideosComponentContextProviderInternal,
	useContext: useVideosComponentContext,
} = createMandatoryContext<VideosComponentContextValueType>(undefined, {
	name: 'VideosComponentContext',
});

export const useVideosComponentContextValue = () => {
	const { uploadStep, setUploadStep } = useUploadStepContext();
	const [petitionVideoList, setPetitionVideoList] = useState<PetitionVideo[]>([]);
	const [initialPetitionVideoListFetchNeeded, setInitialPetitionVideoListFetchNeeded] = useState<boolean>(true);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isSuccessMessageDisplayed, setIsSuccessMessageDisplayed] = useState<boolean>(false);
	const [uploadedVideoId, setUploadedVideoId] = useState('');
	const [isUploading, setIsUploading] = useState<boolean>(false);
	const [videoSource, setVideoSource] = useState<string>('');
	const [hasPlayedAskIntro, setHasPlayedAskIntro] = useState<boolean>(false);

	const value: VideosComponentContextValueType = useMemo(
		() => ({
			data: {
				petitionVideoList,
				initialPetitionVideoListFetchNeeded,
				isUploading,
				uploadStep,
				errorMessage,
				isSuccessMessageDisplayed,
				uploadedVideoId,
				videoSource,
				hasPlayedAskIntro,
			},
			actions: {
				setPetitionVideoList,
				setInitialPetitionVideoListFetchNeeded,
				setUploadStep,
				setErrorMessage,
				setIsSuccessMessageDisplayed,
				setUploadedVideoId,
				setIsUploading,
				setVideoSource,
				setHasPlayedAskIntro,
			},
		}),
		[
			petitionVideoList,
			initialPetitionVideoListFetchNeeded,
			isUploading,
			uploadStep,
			errorMessage,
			isSuccessMessageDisplayed,
			uploadedVideoId,
			videoSource,
			hasPlayedAskIntro,
		],
	);
	return { value };
};
